import React from 'react';
import {
  arrayOf,
  bool,
  func,
  oneOfType,
  shape,
  string,
  symbol
} from 'prop-types';
import {
  ControlLabel,
  FormGroup,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import FontAwesome from 'react-fontawesome';

import Select from 'components/common/Select';
import ErrorMessages from 'components/common/ErrorMessages';

export default function FieldWrapper({
  addonContent,
  input,
  inputComponent,
  inputComponentProps,
  inputWrapper,
  hideLabelInError,
  label,
  meta: { error, invalid, touched, warning, submitError },
  required,
  showAddon,
  showCheckboxLabel,
  showLabel,
  tooltip
}) {
  const validationState =
    (touched && (error || submitError) && 'error') ||
    (warning && 'warning') ||
    null;

  let onBlur;
  if (inputComponent === Select) {
    onBlur = (event) => event.preventDefault();
  }

  const tooltipIcon = (
    <OverlayTrigger
      overlay={<Tooltip id={`${input.name}-tooltip`}>{tooltip}</Tooltip>}
      placement="top"
    >
      <FontAwesome
        name="question-circle"
        tag="i"
        className="inline-block"
        style={{ marginLeft: '5px' }}
      />
    </OverlayTrigger>
  );

  const requiredSymbol = <abbr title="required">*</abbr>;

  const inputComponentWithProps = () => {
    const propsForComponent = {
      ...input,
      ...inputComponentProps,
      value: input.value,
      style: {
        // overrides .input-group class which adds a z-index
        // that will cover popovers from select dropdowns
        zIndex: 'inherit',
        ...inputComponentProps.style
      }
    };

    if (onBlur) propsForComponent.onBlur = onBlur;

    const InputComponent = inputComponent;

    return showCheckboxLabel ? (
      <InputComponent {...propsForComponent}>
        {showCheckboxLabel && label}
      </InputComponent>
    ) : (
      <InputComponent {...propsForComponent} />
    );
  };

  const WrappingComponent = inputWrapper;
  const wrappingElementProps =
    inputWrapper === FormGroup ? { validationState } : {};

  return (
    <WrappingComponent {...wrappingElementProps}>
      {showLabel && (
        <ControlLabel htmlFor={input.name}>
          {required && requiredSymbol} {label}
        </ControlLabel>
      )}
      {tooltip && tooltipIcon}
      <span className={showAddon ? 'input-group input-prepend' : ''}>
        {showAddon && (
          <span className="input-group-addon add-on">{addonContent}</span>
        )}
        {inputComponentWithProps()}
      </span>
      {touched && invalid && (
        <ErrorMessages
          errors={error || submitError}
          hideLabel={hideLabelInError}
        />
      )}
    </WrappingComponent>
  );
}

FieldWrapper.propTypes = {
  addonContent: string,
  input: shape({}).isRequired,
  inputComponent: oneOfType([func, string]).isRequired,
  inputComponentProps: shape({}),
  inputWrapper: oneOfType([func, symbol]),
  hideLabelInError: bool,
  label: string,
  meta: shape({
    error: arrayOf(string),
    invalid: bool,
    submitError: arrayOf(string),
    touched: bool,
    warning: bool
  }).isRequired,
  required: bool,
  showAddon: bool,
  showCheckboxLabel: bool,
  showLabel: bool,
  tooltip: oneOfType([bool, string])
};

FieldWrapper.defaultProps = {
  addonContent: '',
  inputWrapper: FormGroup,
  label: '',
  inputComponentProps: {},
  hideLabelInError: false,
  required: false,
  showAddon: false,
  showCheckboxLabel: false,
  showLabel: false,
  tooltip: false
};
